// Shared layout for all 4 legal pages: Privacy / Terms / Risk / Investor
// Each child page passes title + an array of sections.
// Each section is { heading, children } where children is an array of either
// { type: "p", text } | { type: "ul", items } | { type: "h3", text } | { type: "note", text }.
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale, ExternalLink } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { BrandLogo } from "@/components/BrandLogo";

const LEGAL_NAV = [
  { to: "/legal/terms-of-use",        label: "Master Agreement" },
  { to: "/legal/privacy-policy",      label: "Privacy & Data" },
  { to: "/legal/recording-policy",    label: "Capture Restrictions" },
  { to: "/legal/investor-disclaimer", label: "Investor Access" },
  { to: "/legal/ai-protection",       label: "AI & Trade Secrets" },
  { to: "/legal/risk-disclaimer",     label: "Dispute & Enforcement" },
];

function Block({ block, idx }) {
  if (block.type === "h3") {
    return <h3 key={idx} className="text-white font-['Outfit'] text-base font-semibold mt-5 mb-2">{block.text}</h3>;
  }
  if (block.type === "ul") {
    return (
      <ul key={idx} className="space-y-1.5 mt-2 mb-3 pl-5 list-disc marker:text-[#22C55E]/60">
        {block.items.map((it, i) => (
          <li key={i} className="text-[#C5D5CC] text-sm leading-relaxed">{it}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "note") {
    return (
      <p key={idx} className="text-[#A1B2A8]/70 text-xs italic mt-3 leading-relaxed">
        {block.text}
      </p>
    );
  }
  // default: p
  return <p key={idx} className="text-[#C5D5CC] text-sm leading-relaxed my-2">{block.text}</p>;
}

export default function LegalLayout({ title, eyebrow, effective, lastUpdated, intro, sections, contact }) {
  useEffect(() => { document.title = `${title} — Delta GreenX`; window.scrollTo(0, 0); }, [title]);

  return (
    <main className="bg-[#050A07] min-h-screen text-white" data-testid={`legal-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050A07]/90 backdrop-blur-xl border-b border-[#1A2E22]">
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="legal-back-home" aria-label="Delta GreenX home">
            <BrandLogo as="div" size="md" />
          </Link>
          <Link to="/" className="text-[#A1B2A8] hover:text-white text-xs flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to landing
          </Link>
        </div>
      </header>

      {/* Title */}
      <section className="max-w-[1200px] mx-auto px-6 pt-12 pb-8">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-4 h-4 text-[#22C55E]" />
          <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold">{eyebrow || "Legal"}</p>
        </div>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{title}</h1>
        <div className="text-[#A1B2A8]/60 text-xs mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          {effective && <span><b>Effective Date:</b> {effective}</span>}
          {lastUpdated && <span><b>Last Updated:</b> {lastUpdated}</span>}
        </div>
      </section>

      {/* Inter-page nav */}
      <section className="max-w-[1200px] mx-auto px-6 pb-8">
        <div className="flex flex-wrap gap-2" data-testid="legal-page-nav">
          {LEGAL_NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`text-xs px-3 py-1.5 border transition-colors ${window.location.pathname === n.to ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]" : "border-[#1A2E22] text-[#A1B2A8] hover:text-white hover:border-[#1A2E22]"}`}
              data-testid={`legal-nav-${n.to.split("/").pop()}`}
            >
              {n.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Body */}
      <article className="max-w-[1200px] mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <p className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">On this page</p>
              <ul className="space-y-2 text-xs">
                {sections.map((s, i) => (
                  <li key={i}>
                    <a href={`#section-${i + 1}`} className="text-[#A1B2A8] hover:text-white transition-colors block leading-snug">
                      {`${i + 1}. ${s.heading}`}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div>
            {intro && (
              <div className="border-l-2 border-[#22C55E] pl-5 mb-8">
                {Array.isArray(intro)
                  ? intro.map((p, i) => <p key={i} className="text-[#C5D5CC] text-sm leading-relaxed mb-2">{p}</p>)
                  : <p className="text-[#C5D5CC] text-sm leading-relaxed">{intro}</p>}
              </div>
            )}
            {sections.map((s, i) => (
              <section key={i} id={`section-${i + 1}`} className="mb-10 scroll-mt-24">
                <h2 className="font-['Outfit'] text-xl sm:text-2xl font-semibold mb-3 text-white">
                  <span className="text-[#22C55E] mr-3">{`${i + 1}.`}</span>{s.heading}
                </h2>
                {s.blocks.map((b, j) => <Block key={j} block={b} idx={j} />)}
              </section>
            ))}

            {contact && (
              <section className="border-t border-[#1A2E22] pt-6 mt-12 flex items-start gap-3">
                <ExternalLink className="w-4 h-4 text-[#22C55E] mt-1" />
                <div>
                  <p className="text-white text-sm font-semibold">Contact</p>
                  <p className="text-[#A1B2A8] text-sm mt-1">
                    For legal or privacy concerns:&nbsp;
                    <a href={`mailto:${contact}`} className="text-[#22C55E] hover:underline" data-testid="legal-contact-link">{contact}</a>
                  </p>
                </div>
              </section>
            )}

            <p className="text-[#A1B2A8]/40 text-[10px] italic mt-10 leading-relaxed">
              This document will be replaced by counsel-reviewed final language before live operations begin.
              Continued use of the platform after material updates constitutes acceptance of the revised policy.
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}
